import { ApolloLink } from '@apollo/client';
import { isNotEmptyArray } from '@h5s/util-array';
import {
  isUserFacingError,
  isValidationError,
  TranslatableError,
  UserFacingError,
  ValidationError,
} from '@h5s/util-error';
import { LoggerService } from '@h5s/util-logger';
import { map } from 'rxjs';
import { ErrorService } from './error.service';
import { safeStringify } from '@h5s/util-string';

export function apolloGraphQLFormattedErrorInterceptor(
  loggerService: LoggerService,
  errorService: ErrorService,
): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return forward(operation).pipe(
      map((result) => {
        // ASSERTION: Non sono presenti errori, allora ritorno il result così com'è
        if (!result.errors?.length) return result;

        const logger = loggerService.getLogger('graphQLFormattedErrorLink');

        const graphQLFormattedErrors = result.errors;

        if (!isNotEmptyArray(graphQLFormattedErrors)) return result;

        // Errori di interazione utente
        const userFacingErrors: UserFacingError[] = graphQLFormattedErrors.filter((err) => isUserFacingError(err));
        // Errori di validazione
        const validationErrors: ValidationError[] = graphQLFormattedErrors.filter((err) => isValidationError(err));

        // Estrae le chiavi di traduzione dagli errori di tipo UserFacingError e gli arguments se presenti
        const userFacingErrorsTranslations: TranslatableError[] = userFacingErrors.map((err) => ({
          translationKey: err.extensions.translationKey,
          arguments: Array.isArray(err.extensions.arguments) ? err.extensions.arguments : [],
        }));

        // Errori di validazione form
        const validationErrorsTranslations: TranslatableError[] = graphQLFormattedErrors
          .filter((err) => isValidationError(err))
          .flatMap((err) =>
            err.extensions.formValidationErrors.map((formError) => ({
              translationKey: formError.translationKey,
              arguments: Array.isArray(formError.arguments) ? formError.arguments : [],
            })),
          );

        // Traduciamo e visualizziamo gli errori
        errorService.translateAndDisplayErrors([...userFacingErrorsTranslations, ...validationErrorsTranslations]);

        // Infine logghiamo gli errori restanti
        const remainingErrors = graphQLFormattedErrors.filter(
          (err) => !isUserFacingError(err) && !isValidationError(err),
        );

        remainingErrors.forEach(({ message, path }) => {
          logger.error(`[${path?.join(', ')}] GraphQL error: ${message}`, graphQLFormattedErrors);
        });

        // Ritorniamo tutti gli errori perche anche userFacingErrors e validationErrors possono servire (esempio: progressivo invoice viene rigenerato se già usato)
        const appError = errorService.mapGraphQlFormattedErrorsToAppError(graphQLFormattedErrors);

        appError.info.set('operationName', operation.operationName ?? '');
        appError.info.set('operationVariables', safeStringify(operation.variables));
        appError.info.set('responseBody', safeStringify(result.errors));
        appError.info.set(
          'isUserFacingOrValidationError',
          isNotEmptyArray(userFacingErrors) || isNotEmptyArray(validationErrors),
        );

        errorService.logError(appError);
        throw appError;
      }),
    );
  });
}
